import tinycolor from "tinycolor2";

// El módulo solo se muestra si el backend lo entrega entre los de la empresa
export const qrModuleAllowed = (modules) =>
  Array.isArray(modules) &&
  modules.some((item) => item.moduloGeneral?.ruta_admin === "/qr");

// Completa lo que se escribe en "Enlace de destino", para no obligar a teclear el
// esquema: "google.com" queda "https://www.google.com" y "www.google.com" solo recibe
// "https://". Un enlace que ya trae esquema se devuelve tal cual, también si es uno que
// después se rechaza (ftp://): completarlo lo volvería irreconocible.
//
// El "www." se agrega solo cuando lo escrito es un dominio pelado: una etiqueta más el
// TLD. Con un subdominio ("blog.ejemplo.cl"), un host sin punto ("localhost") o una IP se
// agrega solo "https://", porque anteponerles "www." apuntaría a un host que no existe y
// el QR impreso no llevaría a ninguna parte.
export function completarUrlQr(valor) {
  const texto = String(valor || "").trim();
  if (!texto) return "";
  if (/^[a-z][a-z\d+.-]*:\/\//i.test(texto)) return texto;
  if (/^www\./i.test(texto)) return `https://${texto}`;
  // El host es lo que va antes de la ruta, la consulta o el ancla
  const host = texto.split(/[/?#]/)[0];
  const dominioPelado = /^[a-z\d-]+\.[a-z]{2,}$/i.test(host);
  return dominioPelado ? `https://www.${texto}` : `https://${texto}`;
}

// El destino que se guarda: para WhatsApp se arma el enlace wa.me con el teléfono y el
// mensaje, y para un sitio se completa el esquema antes de validar, así lo que se valida
// es lo mismo que se va a enviar. Lanza Error con el motivo, que las pantallas muestran
// junto al campo.
export function destinationFor(form) {
  if (form.type === "whatsapp") {
    const phone = form.phone.replace(/[\s()+-]/g, "");
    if (!/^[1-9]\d{7,14}$/.test(phone)) {
      throw new Error("Ingresa un teléfono con código de país, entre 8 y 15 dígitos.");
    }
    return `https://wa.me/${phone}?text=${encodeURIComponent(form.message || "")}`;
  }
  let url;
  try {
    url = new URL(completarUrlQr(form.url));
  } catch (_) {
    throw new Error("Ingresa un enlace válido, por ejemplo www.ejemplo.cl.");
  }
  if (!["https:", "http:"].includes(url.protocol) || url.username || url.password) {
    throw new Error("Usa un enlace HTTP o HTTPS sin credenciales.");
  }
  return url.href;
}

// Acepta 3 o 6 dígitos, con "#" o sin él, y devuelve "#rrggbb" en minúsculas; null si no
// es un color
export function normalizeQrColor(value) {
  if (typeof value !== "string") return null;
  const hex = value.trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    const dobles = hex
      .split("")
      .map((char) => char + char)
      .join("");
    return `#${dobles.toLowerCase()}`;
  }
  return /^[0-9a-f]{6}$/i.test(hex) ? `#${hex.toLowerCase()}` : null;
}

// Por qué no sirve el color, o "" si sirve. El mínimo de contraste 4,5 sobre blanco es el
// que aplica el backend, y es lo que hace que el lector distinga el código del fondo.
export function qrColorError(value) {
  const normalized = normalizeQrColor(value);
  if (!normalized) {
    return "Ingresa un color HEX de 3 o 6 caracteres, por ejemplo #0037FF.";
  }
  return tinycolor.readability(normalized, "#ffffff") < 4.5
    ? "Este color tiene poco contraste sobre blanco. Elige un tono más oscuro para guardar el QR."
    : "";
}

// Opciones de los dos selects del editor. Los id son los valores que acepta el backend
// (.QrAdmin/controllers/validation.js): se muestran traducidos, pero se envían tal cual.
export const TIPOS_QR = [
  { id: "url", nombre: "Sitio web" },
  { id: "whatsapp", nombre: "WhatsApp" },
];

export const DISENOS_QR = [
  { id: "square", nombre: "Clásico" },
  { id: "dots", nombre: "Puntos" },
  { id: "rounded", nombre: "Redondeado" },
];

// Colores de los gráficos del módulo. ApexCharts dibuja SVG y no resuelve var(--color-*),
// así que acá sí van en hex: es el azul de marca y la misma paleta de donas ya validada
// que usa el dashboard de EPP (store/modules/epp/dashboard.js).
export const AZUL_GRAFICO = "#0037ff";
// Color de la grilla de los gráficos: el mismo $blue-gray de _variables.scss
export const GRIS_GRAFICO = "#e8e9f3";
export const COLORES_DONA = [
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
];

// Color con que nace un QR nuevo: el azul de marca
export const COLOR_QR_POR_DEFECTO = "#0037ff";

// Colores que el diálogo ofrece junto a los guardados por la empresa. Todos pasan el
// contraste mínimo sobre blanco salvo los dos últimos, que quedan para que se vea el
// aviso: el QR necesita un tono oscuro para que el lector lo distinga del fondo.
export const COLORES_SUGERIDOS_QR = [
  COLOR_QR_POR_DEFECTO,
  "#000000",
  "#3d3d3d",
  "#777777",
  "#c6c6c6",
  "#f1f1f1",
];

// Textos del cupo de códigos QR del plan (store/modules/qr/Cupo.js). `cupo` es el getter
// cupoQr: { limite, usados, alcanzado }.
const codigosQr = (cantidad) => (cantidad === 1 ? "código QR" : "códigos QR");

// "Plan contratado 10 códigos QR / 3 creados", como el límite de pantallas de Canal Corporativo;
// "" si no se conoce el límite o no se pudo contar
export function textoCupoQr({ limite, usados }) {
  if (limite === null || usados === null) return "";
  return `Plan contratado ${limite} ${codigosQr(limite)} / ${usados} ${
    usados === 1 ? "creado" : "creados"
  }`;
}

// Por qué no se puede crear otro. Archivar no libera cupo, así que la salida es ampliar el plan.
export function mensajeCupoQr({ limite }) {
  if (limite === 0) return "Tu plan no incluye códigos QR. Contacta a soporte para agregarlos.";
  return `Tu plan incluye ${limite} ${codigosQr(limite)} y ya están todos creados. Para crear más, contacta a soporte.`;
}

// Límites del backend para un QR, repetidos acá para avisar antes de enviar
export const MAX_NOMBRE_QR = 100;
export const MAX_MENSAJE_QR = 1000;
export const MAX_LOGO_CARACTERES = 350000;
const LADO_LOGO = 256;

// Deja el logo listo para ir encima del QR: cuadrado de 256 px y, sobre todo, con fondo
// blanco. Un PNG con transparencia deja ver los módulos del código a través del logo y el
// lector falla; ni la validación del admin ni la del backend detectan ese caso. La imagen
// entra completa y centrada, nunca recortada: a un logo alargado no se le pueden cortar los
// costados. Devuelve una promesa con el data URL en PNG y falla si no se puede leer o pesa
// más de lo que acepta el backend.
export function normalizarLogoQr(base64) {
  return new Promise((resolve, reject) => {
    const imagen = new Image();
    imagen.onload = () => {
      const lienzo = document.createElement("canvas");
      lienzo.width = LADO_LOGO;
      lienzo.height = LADO_LOGO;
      const contexto = lienzo.getContext("2d");
      contexto.fillStyle = "#ffffff";
      contexto.fillRect(0, 0, LADO_LOGO, LADO_LOGO);
      const escala = Math.min(LADO_LOGO / imagen.width, LADO_LOGO / imagen.height);
      const ancho = imagen.width * escala;
      const alto = imagen.height * escala;
      contexto.drawImage(imagen, (LADO_LOGO - ancho) / 2, (LADO_LOGO - alto) / 2, ancho, alto);
      const resultado = lienzo.toDataURL("image/png");
      if (resultado.length > MAX_LOGO_CARACTERES) {
        reject(new Error("El logo pesa demasiado. Prueba con una imagen más simple."));
        return;
      }
      resolve(resultado);
    };
    imagen.onerror = () =>
      reject(new Error("No se pudo leer la imagen. Prueba con otro PNG o JPG."));
    imagen.src = base64;
  });
}

// Qué falta en cada paso del editor, en una frase; vacío si el paso está completo. Devuelven
// texto en vez de lanzar porque alimentan el botón deshabilitado y la ayuda del pie, que se
// recalculan en cada tecla. Las reglas son las mismas que aplica el backend al guardar.
export function errorContenidoQr(form) {
  const nombre = String(form.name || "").trim();
  if (!nombre) return "Ingresa un nombre para el código QR.";
  if (nombre.length > MAX_NOMBRE_QR)
    return `El nombre no puede superar los ${MAX_NOMBRE_QR} caracteres.`;
  try {
    destinationFor(form);
  } catch (error) {
    return error.message;
  }
  return "";
}

// Las fechas de un QR llegan como milisegundos en texto ("1761768000000"), porque el backend
// declara GraphQLString sobre un Sequelize.DATE; lastScan y las fechas de las carpetas, en
// cambio, llegan en ISO. Esto acepta las dos formas y devuelve un Date, o null si no hay
// fecha: pasarle el texto crudo a moment daría "Invalid date".
export function aFechaQr(valor) {
  if (valor === null || valor === undefined || valor === "") return null;
  const texto = String(valor);
  const fecha = /^\d+$/.test(texto) ? new Date(Number(texto)) : new Date(texto);
  return Number.isNaN(fecha.getTime()) ? null : fecha;
}

export function errorDisenoQr(form) {
  const color = qrColorError(form.color);
  if (color) return color;
  const existe = DISENOS_QR.some((diseno) => diseno.id === form.pattern);
  return existe ? "" : "Elige un diseño para el código QR.";
}
